import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CssBaseline } from '@mui/joy';
import {
  Container,
  Typography as MuiTypography,
} from '@mui/material';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import BarcodePrefixSetting from './widgets/BarcodePrefixSetting';
import DimensionDivisorsSetting from './widgets/DimensionDivisorsSetting';
import CustomerShipmentUnitVisibilitySetting from './widgets/CustomerShipmentUnitVisibilitySetting';

/**
 * Dedicated screen for shipment / piece operational defaults that don't fit
 * the General Settings (site identity / branding) or any of the other
 * specialised settings pages (payment gateways, Twilio, WhatsApp messages).
 *
 * Hosts:
 *  - `BarcodePrefixSetting` — leading characters in newly generated
 *    `ShipmentPiece.barcode` values (FRONTEND_SHIPMENT_PIECES_GUIDE §3).
 *  - `DimensionDivisorsSetting` — `DIM_DIVISOR_AIR` / `DIM_DIVISOR_GROUND`
 *    used by `ShipmentPiece.save()` to compute volumetric weight from
 *    W × L × H (FRONTEND_PER_PIECE_WEIGHT_PRICING_GUIDE §8).
 *
 * Layout intentionally mirrors `WhatsAppMessagesScreen` /
 * `WhatsAppChatBotScreen` / `TwilioSettingsScreen` so the settings module
 * looks consistent. Each widget renders its own Paper "card"; the page
 * provides only the shell (theme, container, page heading).
 */
export default function ShipmentsConfigurationScreen() {
  const { t } = useTranslation('ui');

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <MuiTypography variant="h4" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
                {t('settingsPages.shipmentsConfig.pageTitle')}
              </MuiTypography>
              <MuiTypography variant="body1" color="#6B7280" sx={{ fontSize: '1.1rem' }}>
                {t('settingsPages.shipmentsConfig.pageSubtitle')}
              </MuiTypography>
            </Box>

            <BarcodePrefixSetting />
            <DimensionDivisorsSetting />
            <CustomerShipmentUnitVisibilitySetting />
          </Container>
        </Box>
      </Box>
    </DirectionalMaterialThemeProvider>
  );
}
